import { useConversationStore } from "../store/conversationStore";
import { useChatboxStore } from "@/components/chat/Chatbox/store";
import {
  fromMessageFeedback,
  MessageFeedbackType,
  MessagingParticipantType,
  type MessagingMessage,
} from "@/model/messaging";
import type { deputy_v1_messaging_MessageFeedback as Feedback } from "@deputy-ai/data-platform-api/v1/messaging";
import { Button } from "@deputy-ai/ui/button/Button";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { tap } from "rxjs";

export const MessageFeedback = ({ message }: { message: MessagingMessage }) => {
  const {
    use: { convertedMsgs },
    bind: { sendMessage, updateMessage },
  } = useChatboxStore();

  const feedback = useMessageFeedback(message?.feedbacks);

  const {
    use: { hasHumanAgentHandOffed },
    bind: { sendMessageFeedback },
  } = useConversationStore();

  const visible =
    message?.participant.type === MessagingParticipantType.AIAgent;

  const state = useMemo(() => {
    if (feedback === null) {
      return "ask_for_feedback";
    }
    if (feedback === MessageFeedbackType.THUMB_DOWN) {
      return "show_talk_to_human";
    }
    return "hide";
  }, [feedback]);

  const handleSendFeedback = useCallback(
    (feedback: MessageFeedbackType) => {
      return sendMessageFeedback(message.uid, feedback)
        .pipe(tap((message) => updateMessage(message)))
        .subscribe();
    },
    [message, sendMessageFeedback]
  );

  const t = useTranslations();

  if (
    !visible ||
    state === "hide" ||
    convertedMsgs.length <= 1 ||
    hasHumanAgentHandOffed
  ) {
    return null;
  }

  if (state === "ask_for_feedback") {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="text-destructive border-destructive hover:text-destructive/80 hover:border-destructive/80 hover:bg-destructive/10 not-helpful-btn h-7 rounded-full px-3 py-0.5 text-xs"
            onClick={() => handleSendFeedback(MessageFeedbackType.THUMB_DOWN)}
          >
            {"Not Helpful"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        className="talk-to-human-btn h-7 rounded-full px-3 py-0.5 text-xs"
        onClick={async () => await sendMessage(t("Webchat.talk_to_human"))}
      >
        {t("Webchat.talk_to_human")}
      </Button>
    </div>
  );
};

const useMessageFeedback = (feedbacks?: Feedback[]) => {
  return useMemo(() => {
    const filtered =
      feedbacks
        ?.map(fromMessageFeedback)
        .filter((feedback) => feedback !== null) ?? [];
    if (filtered.length === 0) {
      return null;
    }
    return filtered[0];
  }, [feedbacks]);
};
